import type { GetServerSidePropsContext } from 'next'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkDirective from 'remark-directive'
import { RegularFooter } from 'components/Footer/RegularFooter'
import { CoreNavigation } from 'components/Navigation/consumer'
import { getTermsPage, type TermsPageObject } from 'libs/server/api/terms-pages'
import { getPageData } from 'libs/server/page-data'
import Meta from '../components/Meta'
import { remarkDisableTextDirectives } from '../plugins/remarkDisableTextDirectives'
import { remarkParagraphDirective } from '../plugins/remarkParagraphDirective'
import classes from '../styles.module.scss'

type TermsPageProps = {
  status: 'success'
  termsPage: TermsPageObject
}

const TermsPage = ({ termsPage }: TermsPageProps) => {
  if (!termsPage) {
    return null
  }

  const markdownComponents: Components = {
    h1: ({ children }) => (
      <h1 className='mc-text-h2 mc-text--center'>{children}</h1>
    ),
    h2: ({ children }) => <h2 className='mc-text-h5 mc-mb-4'>{children}</h2>,
    h3: ({ children }) => <h3 className='mc-text--bold'>{children}</h3>,
    h4: ({ children }) => <h4 className='mc-text--bold'>{children}</h4>,
  }

  return (
    <>
      <Meta />
      <div
        id='legal'
        className={`${classes.terms} mc-background--color-light mc-invert mc-py-12`}
      >
        <div className='container'>
          <div className='markdown-content'>
            <ReactMarkdown
              remarkPlugins={[
                // Type cast needed: remark-breaks is a valid remark plugin but its
                // ESM-only types are incompatible with react-markdown's plugin type
                remarkBreaks as typeof remarkDirective,
                remarkDirective,
                remarkDisableTextDirectives,
                remarkParagraphDirective,
              ]}
              components={markdownComponents}
            >
              {termsPage.content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </>
  )
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const slug = ctx.params?.slug

  if (typeof slug !== 'string') {
    return {
      notFound: true,
    }
  }

  return getPageData({
    criticalPromises: {
      termsPage: getTermsPage(ctx, slug),
    },
  })
}

TermsPage.pageSettings = {
  rumLabel: 'terms',
  analyticsLabel: 'Terms',
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export default TermsPage
